import type React from 'react';
import { useContext, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ArrowDownIcon } from '@m/alchemy-ui/icons/ArrowDownIcon';
import { ArrowUpIcon } from '@m/alchemy-ui/icons/ArrowUpIcon';
import { UserContext } from '../../../context';
import { useRoutePath } from '../../../hooks';
import { type ProfileTab } from '../Profile';
import * as Styled from './Menu.styled';

interface TabItem {
  label: React.ReactNode | string;
  url: string;
  isPublic?: boolean;
}

interface DropdownProps {
  readonly title: string;
  readonly list: TabItem[];
  readonly selectedTab: string;
  readonly selected: (tab: string) => void;
  readonly startIndex: number;
  readonly onItemFocusRef: (index: number, el: HTMLElement | null) => void;
  readonly onItemKeyDown: (e: React.KeyboardEvent, index: number) => void;
}

const Dropdown = ({ title, list, selectedTab, selected, startIndex, onItemFocusRef, onItemKeyDown }: DropdownProps) => {
  const isSelected = list?.some(({ url }) => selectedTab.includes(url));
  const [isListOpen, setIsListOpen] = useState(isSelected);
  const toggleList = () => {
    setIsListOpen((prevState) => !prevState);
  };

  if (list.length === 0) return null;

  return (
    <Styled.Wrapper isSelected={isSelected}>
      <Styled.ToggleButton
        role="tab"
        aria-expanded={isListOpen}
        data-testid="dropdown"
        onClick={toggleList}
        isSelected={isSelected}
        ref={(el) => onItemFocusRef(startIndex, el)}
        onKeyDown={(e) => onItemKeyDown(e, startIndex)}
      >
        <div>{title}</div>
        {isListOpen ? <ArrowUpIcon aria-hidden="true" /> : <ArrowDownIcon aria-hidden="true" />}
      </Styled.ToggleButton>
      {isListOpen &&
        list.map((tab, i) => (
          <Styled.Tab
            isSubtab
            role="tab"
            key={tab.url}
            isActive={selectedTab.includes(tab.url)}
            aria-selected={selectedTab.includes(tab.url)}
            onClick={() => selected(tab.url)}
            ref={(el) => onItemFocusRef(startIndex + 1 + i, el)}
            onKeyDown={(e) => onItemKeyDown(e, startIndex + 1 + i)}
          >
            {tab.label}
          </Styled.Tab>
        ))}
    </Styled.Wrapper>
  );
};

export const Menu = ({
  ownerTabs,
  userTabs,
  setSelectedTab,
  userId,
  selectedTab
}: {
  readonly ownerTabs: ProfileTab[];
  readonly userTabs: ProfileTab[];
  readonly setSelectedTab: (arg0: string) => void;
  readonly selectedTab: string;
  readonly userId: string;
}) => {
  const intl = useIntl();
  const currentUser = useContext(UserContext);
  const isProfileOwner = currentUser.id === userId;
  const [addRoutePath] = useRoutePath();

  const tabRefs = useRef<(HTMLElement | null)[]>([]);

  const selectTabHandler = (tab: string) => {
    const url = isProfileOwner ? tab : `${tab}/${userId}`;
    addRoutePath(url);
    setSelectedTab(url);
  };

  const handleArrowNav = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const total = tabRefs.current.length;
      const nextIndex = (index + dir + total) % total;
      tabRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div role="tablist">
      {isProfileOwner ? (
        <>
          {ownerTabs.map((tab, index) => (
            <Styled.Tab
              key={tab.url}
              role="tab"
              isActive={tab.url === selectedTab}
              aria-selected={tab.url === selectedTab}
              onClick={() => selectTabHandler(tab.url)}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              onKeyDown={(e) => handleArrowNav(e, index)}
            >
              {tab.label}
            </Styled.Tab>
          ))}

          <Dropdown
            selectedTab={selectedTab}
            list={userTabs}
            title={intl.formatMessage({ id: 'more', defaultMessage: 'More' })}
            selected={selectTabHandler}
            startIndex={ownerTabs.length}
            onItemFocusRef={(index, el) => {
              tabRefs.current[index] = el;
            }}
            onItemKeyDown={handleArrowNav}
          />
        </>
      ) : (
        userTabs
          .filter(({ isPublic }) => isPublic)
          .map((tab) => (
            <Styled.Tab
              key={tab.url}
              role="tab"
              isActive={selectedTab.includes(tab.url)}
              aria-selected={selectedTab.includes(tab.url)}
              onClick={() => selectTabHandler(tab.url)}
            >
              {tab.label}
            </Styled.Tab>
          ))
      )}
    </div>
  );
};
